import type {
  GitRunStackedActionResult,
  GitStackedAction,
  GitStatusResult,
} from "@cmmd-forge/contracts";
import { isTemporaryWorktreeBranch } from "@cmmd-forge/shared/git";

export type GitActionIconName = "commit" | "push" | "pr";

export type GitDialogAction = "commit" | "push" | "create_pr";

export interface GitActionMenuItem {
  id: "commit" | "push" | "pr";
  label: string;
  disabled: boolean;
  icon: GitActionIconName;
  kind: "open_dialog" | "open_pr";
  dialogAction?: GitDialogAction;
}

export interface GitQuickAction {
  label: string;
  disabled: boolean;
  kind: "run_action" | "run_pull" | "open_pr" | "show_hint";
  action?: GitStackedAction;
  hint?: string;
}

interface GitActionAvailability {
  readonly hasBranch: boolean;
  readonly hasChanges: boolean;
  readonly hasOpenPr: boolean;
  readonly isAhead: boolean;
  readonly isBehind: boolean;
  readonly isDiverged: boolean;
  readonly hasUpstream: boolean;
  readonly hasOriginRemote: boolean;
  readonly isDefaultBranch: boolean;
}

export function resolveQuickActionIconName(
  quickAction: GitQuickAction,
): GitActionIconName | "info" {
  if (quickAction.kind === "open_pr") return "pr";
  if (quickAction.kind === "run_pull") return "info";
  if (quickAction.kind === "run_action") {
    if (quickAction.action === "commit") return "commit";
    if (quickAction.action === "push" || quickAction.action === "commit_push") return "push";
    return "pr";
  }
  return quickAction.label === "Commit" ? "commit" : "info";
}

export interface DefaultBranchActionDialogCopy {
  title: string;
  description: string;
  continueLabel: string;
}

export interface GitActionProgressViewState {
  phaseStartedAtMs: number | null;
  hookStartedAtMs: number | null;
  lastOutputLine: string | null;
}

export type DefaultBranchConfirmableAction =
  | "push"
  | "create_pr"
  | "commit_push"
  | "commit_push_pr";

export function formatElapsedDescription(
  startedAtMs: number | null,
  nowMs = Date.now(),
): string | undefined {
  if (startedAtMs === null) {
    return undefined;
  }
  const elapsedSeconds = Math.max(0, Math.floor((nowMs - startedAtMs) / 1000));
  if (elapsedSeconds < 60) {
    return `Running for ${elapsedSeconds}s`;
  }
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  return `Running for ${minutes}m ${seconds}s`;
}

export function resolveProgressDescription(
  progress: GitActionProgressViewState,
): string | undefined {
  if (progress.lastOutputLine) {
    return progress.lastOutputLine;
  }
  return formatElapsedDescription(progress.hookStartedAtMs ?? progress.phaseStartedAtMs);
}

export function getMenuActionDisabledReason({
  item,
  gitStatus,
  isBusy,
  hasOriginRemote,
}: {
  item: GitActionMenuItem;
  gitStatus: GitStatusResult | null;
  isBusy: boolean;
  hasOriginRemote: boolean;
}): string | null {
  if (!item.disabled) return null;
  if (isBusy) return "Git action in progress.";
  if (!gitStatus) return "Git status is unavailable.";

  if (item.id === "commit") {
    return getCommitDisabledReason(gitStatus);
  }

  if (item.id === "push") {
    return getPushDisabledReason({ gitStatus, hasOriginRemote });
  }

  return getPullRequestDisabledReason({ gitStatus, hasOriginRemote });
}

function getCommitDisabledReason(gitStatus: GitStatusResult): string {
  if (!gitStatus.hasWorkingTreeChanges) {
    return "Worktree is clean. Make changes before committing.";
  }
  return "Commit is currently unavailable.";
}

function getPushDisabledReason(input: {
  readonly gitStatus: GitStatusResult;
  readonly hasOriginRemote: boolean;
}): string {
  if (input.gitStatus.branch === null) {
    return "Detached HEAD: checkout a branch before pushing.";
  }
  if (input.gitStatus.hasWorkingTreeChanges) {
    return "Commit or stash local changes before pushing.";
  }
  if (input.gitStatus.behindCount > 0) {
    return "Branch is behind upstream. Pull/rebase before pushing.";
  }
  if (!input.gitStatus.hasUpstream && !input.hasOriginRemote) {
    return 'Add an "origin" remote before pushing.';
  }
  if (input.gitStatus.aheadCount === 0) {
    return "No local commits to push.";
  }
  return "Push is currently unavailable.";
}

function getPullRequestDisabledReason(input: {
  readonly gitStatus: GitStatusResult;
  readonly hasOriginRemote: boolean;
}): string {
  if (input.gitStatus.pr?.state === "open") {
    return "View PR is currently unavailable.";
  }
  if (input.gitStatus.branch === null) {
    return "Detached HEAD: checkout a branch before creating a PR.";
  }
  if (input.gitStatus.hasWorkingTreeChanges) {
    return "Commit local changes before creating a PR.";
  }
  if (!input.gitStatus.hasUpstream && !input.hasOriginRemote) {
    return 'Add an "origin" remote before creating a PR.';
  }
  if (input.gitStatus.aheadCount === 0) {
    return "No local commits to include in a PR.";
  }
  if (input.gitStatus.behindCount > 0) {
    return "Branch is behind upstream. Pull/rebase before creating a PR.";
  }
  return "Create PR is currently unavailable.";
}

export function buildGitActionProgressStages(input: {
  action: GitStackedAction;
  hasCustomCommitMessage: boolean;
  hasWorkingTreeChanges: boolean;
  pushTarget?: string;
  featureBranch?: boolean;
  shouldPushBeforePr?: boolean;
}): string[] {
  const branchStages = input.featureBranch ? ["Preparing feature branch..."] : [];
  const pushStage = input.pushTarget ? `Pushing to ${input.pushTarget}...` : "Pushing...";
  const prStages = [
    "Preparing PR...",
    "Generating PR content...",
    "Creating GitHub pull request...",
  ];

  if (input.action === "push") {
    return [pushStage];
  }
  if (input.action === "create_pr") {
    return input.shouldPushBeforePr ? [pushStage, ...prStages] : prStages;
  }

  const shouldIncludeCommitStages = input.action === "commit" || input.hasWorkingTreeChanges;
  const commitStages = !shouldIncludeCommitStages
    ? []
    : input.hasCustomCommitMessage
      ? ["Committing..."]
      : ["Generating commit message...", "Committing..."];
  if (input.action === "commit") {
    return [...branchStages, ...commitStages];
  }
  if (input.action === "commit_push") {
    return [...branchStages, ...commitStages, pushStage];
  }
  return [...branchStages, ...commitStages, pushStage, ...prStages];
}

export function buildMenuItems(
  gitStatus: GitStatusResult | null,
  isBusy: boolean,
  hasOriginRemote = true,
): GitActionMenuItem[] {
  if (!gitStatus) return [];

  const hasBranch = gitStatus.branch !== null;
  const hasChanges = gitStatus.hasWorkingTreeChanges;
  const hasOpenPr = gitStatus.pr?.state === "open";
  const isBehind = gitStatus.behindCount > 0;
  const canPushWithoutUpstream = hasOriginRemote && !gitStatus.hasUpstream;
  const canCommit = !isBusy && hasChanges;
  const canPush =
    !isBusy &&
    hasBranch &&
    !hasChanges &&
    !isBehind &&
    gitStatus.aheadCount > 0 &&
    (gitStatus.hasUpstream || canPushWithoutUpstream);
  const canCreatePr =
    !isBusy &&
    hasBranch &&
    !hasChanges &&
    !hasOpenPr &&
    gitStatus.aheadCount > 0 &&
    !isBehind &&
    (gitStatus.hasUpstream || canPushWithoutUpstream);
  const canOpenPr = !isBusy && hasOpenPr;

  return [
    {
      id: "commit",
      label: "Commit",
      disabled: !canCommit,
      icon: "commit",
      kind: "open_dialog",
      dialogAction: "commit",
    },
    {
      id: "push",
      label: "Push",
      disabled: !canPush,
      icon: "push",
      kind: "open_dialog",
      dialogAction: "push",
    },
    hasOpenPr
      ? {
          id: "pr",
          label: "View PR",
          disabled: !canOpenPr,
          icon: "pr",
          kind: "open_pr",
        }
      : {
          id: "pr",
          label: "Create PR",
          disabled: !canCreatePr,
          icon: "pr",
          kind: "open_dialog",
          dialogAction: "create_pr",
        },
  ];
}

export function resolveQuickAction(
  gitStatus: GitStatusResult | null,
  isBusy: boolean,
  isDefaultBranch = false,
  hasOriginRemote = true,
): GitQuickAction {
  const unavailable = resolveUnavailableQuickAction({ gitStatus, isBusy });
  if (unavailable) return unavailable;
  if (!gitStatus) return unavailableGitStatusQuickAction();

  const availability = resolveGitActionAvailability({
    gitStatus,
    isDefaultBranch,
    hasOriginRemote,
  });

  if (!availability.hasBranch) {
    return {
      label: "Commit",
      disabled: true,
      kind: "show_hint",
      hint: "Create and checkout a branch before pushing or opening a PR.",
    };
  }

  if (availability.hasChanges) {
    return resolveDirtyQuickAction(availability);
  }

  if (!availability.hasUpstream) {
    return resolveNoUpstreamQuickAction(availability);
  }

  if (availability.isDiverged) {
    return {
      label: "Sync branch",
      disabled: true,
      kind: "show_hint",
      hint: "Branch has diverged from upstream. Rebase/merge first.",
    };
  }

  if (availability.isBehind) {
    return {
      label: "Pull",
      disabled: false,
      kind: "run_pull",
    };
  }

  if (availability.isAhead) {
    return resolveAheadQuickAction(availability);
  }

  if (availability.hasOpenPr && availability.hasUpstream) {
    return { label: "View PR", disabled: false, kind: "open_pr" };
  }

  return {
    label: "Commit",
    disabled: true,
    kind: "show_hint",
    hint: "Branch is up to date. No action needed.",
  };
}

function resolveUnavailableQuickAction(input: {
  readonly gitStatus: GitStatusResult | null;
  readonly isBusy: boolean;
}): GitQuickAction | null {
  if (input.isBusy) {
    return { label: "Commit", disabled: true, kind: "show_hint", hint: "Git action in progress." };
  }
  if (!input.gitStatus) {
    return unavailableGitStatusQuickAction();
  }
  return null;
}

function unavailableGitStatusQuickAction(): GitQuickAction {
  return {
    label: "Commit",
    disabled: true,
    kind: "show_hint",
    hint: "Git status is unavailable.",
  };
}

function resolveGitActionAvailability(input: {
  readonly gitStatus: GitStatusResult;
  readonly isDefaultBranch: boolean;
  readonly hasOriginRemote: boolean;
}): GitActionAvailability {
  const isAhead = input.gitStatus.aheadCount > 0;
  const isBehind = input.gitStatus.behindCount > 0;
  return {
    hasBranch: input.gitStatus.branch !== null,
    hasChanges: input.gitStatus.hasWorkingTreeChanges,
    hasOpenPr: input.gitStatus.pr?.state === "open",
    isAhead,
    isBehind,
    isDiverged: isAhead && isBehind,
    hasUpstream: input.gitStatus.hasUpstream,
    hasOriginRemote: input.hasOriginRemote,
    isDefaultBranch: input.isDefaultBranch,
  };
}

function resolveDirtyQuickAction(availability: GitActionAvailability): GitQuickAction {
  if (!availability.hasUpstream && !availability.hasOriginRemote) {
    return { label: "Commit", disabled: false, kind: "run_action", action: "commit" };
  }
  if (availability.hasOpenPr || availability.isDefaultBranch) {
    return { label: "Commit & push", disabled: false, kind: "run_action", action: "commit_push" };
  }
  return {
    label: "Commit, push & PR",
    disabled: false,
    kind: "run_action",
    action: "commit_push_pr",
  };
}

function resolveNoUpstreamQuickAction(availability: GitActionAvailability): GitQuickAction {
  if (!availability.hasOriginRemote) {
    return resolveNoOriginQuickAction(availability);
  }
  if (!availability.isAhead) {
    return availability.hasOpenPr
      ? { label: "View PR", disabled: false, kind: "open_pr" }
      : {
          label: "Push",
          disabled: true,
          kind: "show_hint",
          hint: "No local commits to push.",
        };
  }
  return resolveAheadQuickAction(availability);
}

function resolveNoOriginQuickAction(availability: GitActionAvailability): GitQuickAction {
  if (availability.hasOpenPr && !availability.isAhead) {
    return { label: "View PR", disabled: false, kind: "open_pr" };
  }
  return {
    label: "Push",
    disabled: true,
    kind: "show_hint",
    hint: 'Add an "origin" remote before pushing or creating a PR.',
  };
}

function resolveAheadQuickAction(availability: GitActionAvailability): GitQuickAction {
  if (availability.hasOpenPr || availability.isDefaultBranch) {
    return {
      label: "Push",
      disabled: false,
      kind: "run_action",
      action: availability.isDefaultBranch ? "commit_push" : "push",
    };
  }
  return {
    label: "Push & create PR",
    disabled: false,
    kind: "run_action",
    action: "create_pr",
  };
}

export function requiresDefaultBranchConfirmation(
  action: GitStackedAction,
  isDefaultBranch: boolean,
): boolean {
  if (!isDefaultBranch) return false;
  return (
    action === "push" ||
    action === "create_pr" ||
    action === "commit_push" ||
    action === "commit_push_pr"
  );
}

export function resolveDefaultBranchActionDialogCopy(input: {
  action: DefaultBranchConfirmableAction;
  branchName: string;
  includesCommit: boolean;
}): DefaultBranchActionDialogCopy {
  const branchLabel = input.branchName;
  const suffix = ` on "${branchLabel}". You can continue on this branch or create a feature branch and run the same action there.`;

  if (input.action === "push" || input.action === "commit_push") {
    if (input.includesCommit) {
      return {
        title: "Commit & push to default branch?",
        description: `This action will commit and push changes${suffix}`,
        continueLabel: `Commit & push to ${branchLabel}`,
      };
    }
    return {
      title: "Push to default branch?",
      description: `This action will push local commits${suffix}`,
      continueLabel: `Push to ${branchLabel}`,
    };
  }

  if (input.includesCommit) {
    return {
      title: "Commit, push & create PR from default branch?",
      description: `This action will commit, push, and create a PR${suffix}`,
      continueLabel: `Commit, push & create PR`,
    };
  }
  return {
    title: "Push & create PR from default branch?",
    description: `This action will push local commits and create a PR${suffix}`,
    continueLabel: "Push & create PR",
  };
}

export function resolveThreadBranchUpdate(
  result: GitRunStackedActionResult,
): { branch: string } | null {
  if (result.branch.status !== "created" || !result.branch.name) {
    return null;
  }

  return {
    branch: result.branch.name,
  };
}

export function resolveLiveThreadBranchUpdate(input: {
  threadBranch: string | null;
  gitStatus: GitStatusResult | null;
}): { branch: string | null } | null {
  if (!input.gitStatus) {
    return null;
  }

  if (input.gitStatus.branch === null && input.threadBranch !== null) {
    return null;
  }

  if (input.threadBranch === input.gitStatus.branch) {
    return null;
  }

  if (
    input.threadBranch !== null &&
    input.gitStatus.branch !== null &&
    !isTemporaryWorktreeBranch(input.threadBranch) &&
    isTemporaryWorktreeBranch(input.gitStatus.branch)
  ) {
    return null;
  }

  return {
    branch: input.gitStatus.branch,
  };
}

// Re-export from shared for backwards compatibility in this module's exports
export { resolveAutoFeatureBranchName } from "@cmmd-forge/shared/git";
